import { Fragment, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { TopBar } from "@/components/TopBar";
import { useSidebarState } from "@/lib/sidebarState";
import { api } from "@/services/api";
import { semanticColors, layout, motion } from "@/styles/tokens";

export function Layout() {
  const location = useLocation();
  // Configurable identity (P1-12): one settings read when the signed-in shell
  // mounts, so the product name, the money format and the numbering apply on
  // EVERY page, not only on pages that happen to read settings. The read
  // applies them as a side effect; a failure changes nothing (the build's own
  // defaults stay, which for the default deployment are today's values).
  useEffect(() => {
    void api.getCompanySettingsFromBackend().catch(() => undefined);
  }, []);
  // sidebar-and-logo: full (>= 1280) / icon rail (768-1279) / phone slide-in
  // menu, plus the user's remembered collapse choice -- see lib/sidebarState.
  const { mode, collapsed, toggle, drawerOpen, openDrawer, closeDrawer } = useSidebarState();
  const phone = mode === "phone";
  // The content column starts where the sidebar ends. On a phone the sidebar
  // is off-screen (a slide-in menu), so the content is full width.
  const sidebarWidth = phone ? 0 : (collapsed ? layout.sidebarWidthCollapsed : layout.sidebarWidth);

  return (
    <div
      data-testid="app-shell"
      // h-screen (100vh) is the FALLBACK. The inline height below overrides
      // it with 100dvh where supported, and is simply dropped by browsers
      // that don't understand the unit.
      className="h-screen flex flex-col overflow-hidden"
      style={{
        // 100dvh, not 100vh. On mobile browsers 100vh is the LARGE viewport
        // — the height with the URL bar hidden — which is taller than what
        // is actually on screen. That makes the shell overflow the visible
        // area, the document itself becomes scrollable, and the fixed
        // sidebar shifts against that scroll instead of staying put: the
        // "sidebar only appears once you scroll" bug. 100dvh tracks the
        // visible area as browser chrome shows and hides, so the shell is
        // always exactly the viewport and there is nothing to scroll.
        height: "100dvh",
        backgroundColor: semanticColors.surface,
        color: semanticColors.textPrimary,
      }}
    >
      <Sidebar
        collapsed={collapsed}
        onToggle={toggle}
        drawer={phone}
        open={drawerOpen}
        onClose={closeDrawer}
      />
      {/* One frame: the sidebar header and this top bar are the same height. */}
      <div
        className="flex flex-col flex-1 min-h-0 min-w-0"
        style={{ marginLeft: sidebarWidth, transition: `margin-left ${motion.layoutShift}` }}
      >
      <TopBar phone={phone} menuOpen={drawerOpen} onMenu={drawerOpen ? closeDrawer : openDrawer} />
      <div className="flex flex-1 min-h-0">
        <main
          className="flex-1 min-w-0 overflow-y-auto"
          style={{ backgroundColor: semanticColors.pageBg }}
        >
          {/* Keyed on the search string so a top-bar search that lands on the
              page you are already on (/quotes?q=, /catalog?q=) remounts it
              and it reads the new q; a plain navigation is unchanged. */}
          <Fragment key={location.pathname === "/quotes" || location.pathname === "/catalog" ? location.search : ""}>
            <Outlet />
          </Fragment>
        </main>
      </div>
      </div>
    </div>
  );
}
